import { alpha } from '@mui/material/styles';
import { Box, ButtonBase, Typography } from '@mui/material';

export interface LanguageVariantTab {
  /** Locale code, e.g. `en-US`. */
  code: string;
  /** Display label (translated), e.g. "English (United States)". */
  label: string;
  /** Per-language source badge: `System` | `Override` (translated label). */
  badge: string;
  /** True renders the badge in the accent (Override) treatment. */
  badgeAccent?: boolean;
}

export interface LanguageVariantTabsProps {
  tabs: LanguageVariantTab[];
  value: string;
  onChange: (code: string) => void;
  /** Caption under the tab row (translated), e.g. the independent-history note. */
  caption?: string;
}

/**
 * The template editor's language tab row: each language is a separate template
 * row with its own version history, so each tab carries its own Source badge.
 * Port of the prototype editor's language-variant tabs.
 */
export function LanguageVariantTabs({ tabs, value, onChange, caption }: LanguageVariantTabsProps) {
  return (
    <Box>
      <Box
        sx={{ display: 'flex', gap: 0.5, borderBottom: 1, borderColor: 'divider' }}
        role="tablist"
      >
        {tabs.map((tab) => {
          const selected = tab.code === value;
          return (
            <ButtonBase
              key={tab.code}
              role="tab"
              aria-selected={selected}
              onClick={() => onChange(tab.code)}
              sx={(theme) => ({
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                px: 1.75,
                py: 1.25,
                mb: '-1px',
                fontSize: 13,
                fontWeight: selected ? 700 : 500,
                color: selected ? 'text.primary' : 'text.secondary',
                borderBottom: `2px solid ${selected ? theme.palette.secondary.main : 'transparent'}`,
              })}
            >
              {tab.label}
              <Box
                component="span"
                sx={(theme) => ({
                  fontSize: 10,
                  fontWeight: 600,
                  px: 0.75,
                  py: 0.25,
                  borderRadius: '4px',
                  color: tab.badgeAccent
                    ? theme.palette.secondary.dark
                    : theme.palette.text.secondary,
                  bgcolor: tab.badgeAccent
                    ? alpha(theme.palette.secondary.main, 0.12)
                    : theme.palette.background.default,
                  border: `1px solid ${
                    tab.badgeAccent
                      ? alpha(theme.palette.secondary.main, 0.3)
                      : theme.palette.divider
                  }`,
                })}
              >
                {tab.badge}
              </Box>
            </ButtonBase>
          );
        })}
      </Box>
      {caption ? (
        <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mt: 0.75 }}>
          {caption}
        </Typography>
      ) : null}
    </Box>
  );
}
